<template>
  <div class="quota-widget flex flex-col gap-4" :class="{ chromeless: props.chromeless }" :data-module="props.module" v-if="current.storage.limit > 0 || props.showUnlimited">
    <!-- special case: show "Unlimited" (e.g. portal widget) -->
    <template v-if="current.storage.limit <= 0 && props.showUnlimited">
      <div class="flex flex-row items-end">
        <div class="flex-grow me-2 font-medium text-accent-auto">{{ props.title || triggerConfig.title }}</div>
        <div class="text-right">
          <span class="font-bold text-accent-auto" data-test="unlimited">{{ i18n.unlimited }}</span>
        </div>
      </div>
    </template>
    <!-- normal widget with limit -->
    <template v-else-if="current.storage.limit > 0">
      <div class="flex flex-col gap-2">
        <!-- Storage consumption -->
        <div class="flex flex-row items-end">
          <div class="flex-grow me-2 font-medium">{{ props.title || triggerConfig.title }}</div>
          <div class="text-right">
            <span class="text-gray"><span data-test="used">{{ humanized.used }}</span> / </span>
            <span class="font-bold" data-test="limit">{{ humanized.limit }}</span>
          </div>
        </div>
        <div class="progress m-0 h-2 bg-accent-100">
          <div class="progress-bar" role="progressbar"
            :aria-label="i18n.usageInPercent"
            aria-valuemin="0"
            aria-valuemax="100"
            :aria-valuenow="current.usageInPercent"
            data-test="progressbar"
            :style="`width: ${current.usageInPercent}%; background: ${current.barColor};`"
          ></div>
        </div>
        <div v-if="props.showDescription && description" class="text-xs text-gray" data-test="description">{{ description }}</div>
      </div>
      <!-- Count-based warning -->
      <div v-if="props.showCountQuota && countWarning" class="text-xs text-gray" data-test="warning">
        <span class="font-bold text-attention">{{ i18n.warning }}</span> {{ countWarning }}
      </div>
      <!-- Upsell message -->
      <template v-if="showUpsell">
        <div v-if="message" class="text-xs font-medium" data-test="message">{{ message }}</div>
        <button v-if="triggerConfig.upsellActionType === 'button'" type="button" class="btn btn-default w-full" @click="triggerUpgrade()" data-test="cta">{{ upsellActionTitle }}</button>
        <a v-else-if="triggerConfig.upsellActionType === 'link'" href="#" role="button" @click.prevent="triggerUpgrade()"  data-test="cta">{{ upsellActionTitle }}</a>
      </template>
    </template>
  </div>
  <!-- Upsell rocket -->
  <button v-if="showUpsell && triggerConfig.upsellActionType === 'rocket'" class="rocket-box w-full p-16 my-16 flex flex-row flex-center" data-test="cta" @click="triggerUpgrade()">
    <illustration src="illustrations/rocket.svg" :width="24" height="auto" class="me-16 shrink-0 opacity-100"></illustration>
    <div class="font-medium text-accent-auto">{{ upsellActionTitle }}</div>
  </button>
</template>
<script setup>
import illustration from '@/io.ox/vueComponents/illustration.vue'
import _ from '@/underscore'
import { computed } from 'vue'
import { useQuotaStore } from '@/pe/quota/quota-store.js'
import strings, { bytesToUnit } from '@/io.ox/core/strings'
import locale from '@/io.ox/core/locale'
import upsell from '@/io.ox/core/upsell.js'
import gt from 'gettext'

const i18n = {
  upgrade: gt('Upgrade'),
  usageInPercent: gt('Usage in percent'),
  title: gt('Storage'),
  warning: gt('Warning:'),
  unlimited: gt('Unlimited')
}

const store = useQuotaStore()
const props = defineProps({
  module: { type: String, default: 'mail' },
  // portal widget does not show upsell (twice)
  showUpsell: { type: Boolean, default: true },
  // show description text
  showDescription: { type: Boolean, default: true },
  // show quota for mail or files (also portal widget)
  showCountQuota: { type: Boolean, default: true },
  // custom title (also portal widget)
  title: { type: String, default: '' },
  // no background/border/padding (also portal widget)
  chromeless: { type: Boolean, default: false },
  // always show 'unlimited' when limit <= 0
  showUnlimited: { type: Boolean, default: false },
  // id for filestorage accounts (hiDrive)
  accountId: { type: String, default: '' }
})

const isMail = props.module === 'mail'
const triggerId = isMail ? 'mail-folderview-quota' : 'files-folderview-quota'
const defaultBarColor = 'var(--progress-bar)'
// #. quota widget upsell button label
const defaultUpsellActionTitle = gt('Upgrade')

const triggerConfig = {
  // bar colors
  barColors: { 0: defaultBarColor, 80: 'var(--progress-bar-80)', 90: 'var(--progress-bar-90)' },
  // in percent, when to show the count warning
  countThreshold: 90,
  // #. %1$d is the email count limit
  // #. %2$d is the email count consumption in percent
  // #. Example: There is an additional limit of 500 emails. You have already reached 90%.
  countWarning: gt('There is an additional limit of %1$d emails. You have already reached %2$d%.'),
  // #. quota widget description text
  // #. %1$d is storage consumption in percent
  // #. %2$d is remaining storage (e.g. 2 GB)
  // #. Example: 20% [of your storage] used, 8 GB remaining.
  description: gt('%1$d% used, %2$s remaining.'),
  // enabled for upsell
  enabled: true,
  // #. quota widget title
  title: isMail ? gt('Mail storage') : gt('File storage'),
  // button/link title
  upsellActionTitle: defaultUpsellActionTitle,
  // 'button' or 'link'
  upsellActionType: 'button',
  // optional upsell trigger condition
  upsellCondition: '',
  // upper limit for storage upsell (e.g. top offering). -1 means no limit
  upsellLimit: -1,
  // #. first upsell message
  upsellMessage1: gt('Unlock extra storage with an upgrade!'),
  // second upsell message
  upsellMessage2: isMail
    // #. second upsell teaser text (for mail) when storage gets full
    ? gt('Your inbox is getting full. Unlock extra storage with an upgrade!')
    : '',
  // in percent, when to show the first message
  upsellThreshold1: 0,
  // in percent, when to show the second message
  upsellThreshold2: 80,
  // allow customization
  ...upsell.getTriggerConfig(triggerId)
}

const barColors = _.isObject(triggerConfig.barColors)
  ? Object.entries(triggerConfig.barColors).map(([key, value]) => [Number(key) || 0, value]).sort((a, b) => b[0] - a[0])
  : [[0, defaultBarColor]]

const current = computed(() => {
  let data
  switch (props.module) {
    case 'mail':
      data = store.getMailQuota()
      break
    case 'files':
      data = store.getFileQuota()
      break
    default:
      data = store.getAccountQuota(props.module, props.accountId)
  }
  const limit = data.value.quota
  const used = Math.min(Math.max(0, limit), data.value.use)
  const usageInPercent = limit > 0 ? Math.ceil(used / limit * 100) : 0
  const barColor = barColors.find(([threshold]) => usageInPercent >= threshold)?.at(1)
  return {
    barColor,
    count: { used: data.value.countuse, limit: data.value.countquota },
    storage: { used, limit },
    usageInPercent
  }
})

const humanized = computed(() => {
  const { used, limit } = current.value.storage
  const unitUsed = bytesToUnit(used)
  const unitLimit = bytesToUnit(limit)
  const sameUnit = unitUsed.unit === unitLimit.unit
  return {
    used: sameUnit ? unitUsed.number : `${unitUsed.number}\u00a0${unitUsed.unit}`,
    limit: `${unitLimit.number}\u00a0${unitLimit.unit}`
  }
})

const showUpsell = computed(() => {
  if (!props.showUpsell) return false
  if (!upsell.isActivated()) return false
  if (!triggerConfig.enabled) return false
  if (current.value.storage.limit <= 0) return false
  if (triggerConfig.upsellCondition && !upsell.shouldShowUpsellTrigger(triggerConfig.upsellCondition)) return false
  if (triggerConfig.upsellLimit <= 0) return true
  return current.value.storage.limit < triggerConfig.upsellLimit
})

const description = computed(() => {
  if (!triggerConfig.description) return ''
  const remaining = Math.max(0, current.value.storage.limit - current.value.storage.used)
  return _.printf(triggerConfig.description, current.value.usageInPercent, humanizeFileSize(remaining))
})

const countWarning = computed(() => {
  const limit = current.value.count.limit
  if (limit <= 0) return ''
  const used = current.value.count.used
  const countUsageInPercent = limit > 0 ? Math.ceil(used / limit * 100) : 0
  if (countUsageInPercent < triggerConfig.countThreshold) return ''
  return _.printf(triggerConfig.countWarning, locale.number(limit), countUsageInPercent)
})

const message = computed(() => {
  if (!showUpsell.value) return ''
  const usage = current.value.usageInPercent
  // second message?
  const { upsellMessage2, upsellThreshold2 } = triggerConfig
  if (upsellMessage2 && usage >= upsellThreshold2) return upsellMessage2
  // first message?
  const { upsellMessage1, upsellThreshold1 } = triggerConfig
  if (upsellMessage1 && usage >= upsellThreshold1) return upsellMessage1
  // no message
  return ''
})

const upsellActionTitle = triggerConfig.upsellActionTitle || defaultUpsellActionTitle

function humanizeFileSize (size) {
  return strings.fileSize(size, 'smart/1').replace(/\s/g, '\u00A0') // non-breaking space
}

function triggerUpgrade () {
  upsell.trigger({ type: 'custom', id: triggerId, missing: 'storage' })
}

</script>

<style scoped>
.quota-widget {
  --neutral-100: oklch(97% 0 0 / 70%);
  --neutral-200: oklch(92.2% 0 0 / 70%);
  --neutral-300: oklch(87% 0 0 / 70%);
  background-color: var(--neutral-100);
  border: 1px solid var(--neutral-300);
  border-radius: var(--default-radius);
  padding: 1rem;
}
html.dark .quota-widget {
  --neutral-900: oklch(20.5% 0 0);
  --neutral-800: oklch(26.9% 0 0);
  background-color: var(--neutral-900);
  border: 1px solid var(--neutral-800);
}
html .quota-widget.chromeless {
  background-color: transparent;
  border: none;
  padding: 0;
}
.p-4 { padding: 1rem; }
.h-2 { height: 0.5rem; }
.me-2 { margin-inline-end: 0.5rem; }
.gap-2 { gap: 0.5rem; }
.gap-4 { gap: 1rem; }
.items-end { align-items: flex-end; }
.text-gray { color: #707070}
.text-accent-auto { color: var(--text-accent); }

.progress { background-color: var(--neutral-300); }
html.dark .progress { background-color: var(--background); }
</style>
